/**
 * Stage-0 prefilter flags (E06-F07) — free code rules that MARK a long-list row with
 * a reason instead of removing it. Spec:
 * docs/superpowers/specs/2026-10-20-research-jev-triage-design.md §5.1.
 *
 * A row carries a LIST of flags (one per rule that fired), stored under the private
 * `evidence.__discovery.prefilter` key and projected onto the Review row as
 * `prefilterFlags`. Rules: `name` (E06-F07-T01 — the company name looks like a
 * nonprofit / distributor / service / staffing firm); `geo` (E06-F07-T02 — the HQ
 * state is a Do-Not-Source state of the brief or outside the brief's states).
 *
 * E06-F07-T02 — "Return" (a person clears a flag for this request) is stored as a
 * DISMISSAL under `evidence.__discovery.prefilterDismissed`, keyed by
 * {@link prefilterFlagKey} (rule + reason). A re-run of the prefilters (criteria
 * edited) never puts a dismissed flag back; a flag with another key (another rule or
 * another reason) still appears.
 */
import { z } from 'zod';
export const prefilterFlagSchema = z.object({
    /** The rule that set the flag (`name`, `geo`). */
    rule: z.string().min(1),
    /** Human reason shown at the row ("Name looks like a distributor"). */
    reason: z.string().min(1),
    /** Rule-specific category (name rule: the dictionary type; geo: `dns` / `outside`). */
    type: z.string().optional(),
    /** What matched (name rule: the dictionary term; geo: the HQ state). */
    matched: z.string().optional(),
});
/** Parse a stored flag list leniently: malformed entries are dropped. Pure. */
export function parsePrefilterFlags(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const item of value) {
        const parsed = prefilterFlagSchema.safeParse(item);
        if (parsed.success)
            out.push(parsed.data);
    }
    return out;
}
/**
 * E06-F07-T02 — the identity of a flag for a manual dismissal: rule + reason
 * (case/space-insensitive). The same rule firing for the same reason again is the
 * SAME flag (stays dismissed); another reason is a new flag. Pure.
 */
export function prefilterFlagKey(flag) {
    const norm = (s) => s.trim().replace(/\s+/g, ' ').toLowerCase();
    return `${norm(flag.rule)}|${norm(flag.reason)}`;
}
/** E06-F07-T02 — one manual "Return" of a flag on a row (who + when + what). */
export const prefilterDismissalSchema = z.object({
    /** {@link prefilterFlagKey} of the dismissed flag. */
    key: z.string().min(1),
    rule: z.string().min(1),
    reason: z.string().min(1),
    /** The user who returned the row (session identity). */
    by: z.string().nullable().default(null),
    /** ISO timestamp. */
    at: z.string().nullable().default(null),
});
/** Parse a stored dismissal list leniently: malformed entries are dropped. Pure. */
export function parsePrefilterDismissals(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const item of value) {
        const parsed = prefilterDismissalSchema.safeParse(item);
        if (parsed.success)
            out.push(parsed.data);
    }
    return out;
}
/**
 * E06-F07-T02 — POST /research/:requestId/review/:rowId/prefilter/dismiss and
 * …/prefilter/restore. `key` = one flag ({@link prefilterFlagKey}); absent = every
 * flag of the row (dismiss) / every dismissal of the row (restore).
 */
export const prefilterFlagActionBodySchema = z
    .object({ key: z.string().trim().min(1).max(600).optional() })
    .default({});
/** E06-F07-T02 — POST /research/:requestId/review/prefilters/rerun result. */
export const prefilterRerunResponseSchema = z.object({
    success: z.literal(true).optional(),
    /** Rows of the request looked at. */
    rows: z.number().int().nonnegative(),
    /** Rows whose stored flag list changed. */
    changed: z.number().int().nonnegative(),
    /** Rows carrying at least one flag after the run. */
    flagged: z.number().int().nonnegative(),
    /** Rows skipped because the AI check was running on them. */
    skipped: z.number().int().nonnegative(),
});
//# sourceMappingURL=research-prefilter.js.map